/**
 * Weak check for existent fields
 * @param data 
 */
export function parseFalsyFields(data: { value: any, name: string }[], params?: { checkOnlyIfDefined: boolean }): string[] {
  const falsy = [];
  for (const d of data) {
    if (params && params.checkOnlyIfDefined) {
      if (d.value !== undefined && !d.value) falsy.push(d.name);
    } else {
      if (!d.value) falsy.push(d.name);
    }
  }
  return falsy;
}

/**
 * Check valid fields
 * @param data 
 */
export function parseInvalidFields(data: { value: any, name: string, validateFn: (v: any) => string }[], params?: { checkOnlyIfDefined: boolean }): string[] {
  const falsy = [];
  for (const d of data) {
    if (params && params.checkOnlyIfDefined) {
      if (d.value !== undefined && d.validateFn(d.value)) falsy.push(d.name);
    } else {
      if (d.validateFn(d.value)) falsy.push(d.name);
    }
  }
  return falsy;
}

/**
 * Non negative check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function falsyValidationFn(): (v: any) => string {
  return (v: any): string => {
    return Boolean(v) ? '' : 'Required field'
  }
}

/**
 * Non negative check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function nonNegativeValidationFn(errorString?: string): (v: number) => string {
  return (v: number): string => {
    return v >= 0 ? '' : (errorString ? errorString : 'Only positive value is allowed');
  }
}

/**
 * Value check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function lessThanValueValidationFn(maxValue: number, errorString?: string): (v: number) => string {
  return (v: number): string => {
    return v <= maxValue ? '' : (errorString ? errorString : 'Should be less than ' + maxValue);
  }
}

/**
 * Value check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function greaterThanValueValidationFn(maxValue: number, errorString?: string): (v: number) => string {
  return (v: number): string => {
    return v > maxValue ? '' : (errorString ? errorString : 'Should be more than ' + maxValue);
  }
}

/**
 * Value check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function greaterThanEqualToValueValidationFn(maxValue: number, errorString?: string): (v: number) => string {
  return (v: number): string => {
    return v >= maxValue ? '' : (errorString ? errorString : 'Should be more than ' + maxValue);
  }
}

/**
 * Value check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function betweenValuesValidationFn(minValue: number, maxValue: number, errorString?: string): (v: number) => string {
  return (v: number): string => {
    return v <= maxValue && v >= minValue ? '' : (errorString ? errorString : `Should be between ${minValue} and ${maxValue}`);
  }
}

/**
 * Digit count check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function maxDigitCountValidationFn(count: number, errorString?: string): (v: number) => string {
  return (v: number): string => {
    return v.toString().length <= count ? '' : (errorString ? errorString : `${count} digits allowed`);
  }
}

/**
 * Digit count check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function exactDigitCountValidationFn(count: number, errorString?: string): (v: number) => string {
  return (v: number): string => {
    return v.toString().length === count ? '' : (errorString ? errorString : `${count} digits required`);
  }
}

/**
 * Phone number check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function phoneValidationFn(optional?: boolean): (s: string) => string {
  return (s: string): string => {
    if (optional && !s) return '';
    return isValidPhone(s) ? '' : 'Invalid phone number'
  }
}

/**
 * Non negative check validation function to be used with gbk field
 * @param v 
 * @returns 
 */
export function mailValidationFn(): (s: string) => string {
  return (s: string): string => {
    return isValidMail(s) ? '' : 'Invalid email address'
  }
}

/**
 * Validate if given string is a valid Indian phone number
 * @param s 
 * @returns 
 */
export function isValidPhone(s: string): boolean {
  return s ? Boolean((String(s)).match(/^[6789]\d{9}$/)) : false;
}

/**
 * Validate if given string is a valid Indian phone number
 * @param s 
 * @returns 
 */
export function isValidMail(s: string): boolean {
  return s ? Boolean(s.match(/^\S+@\S+\.\S+$/)) : false;
}